import { useCallback, useMemo } from "react";
import { useUserState } from "../state/user";
import {
  CreateAddressInput,
  PhoneNumberInput,
  addAddress,
  addEmail,
  addPersonalPhone,
  removeAddress,
  removeEmail,
  removePersonalPhone
} from "../services/professionals";

/**
 * The mutations that change the professional's own profile.
 *
 * Every one of them changes what `user` holds, and every screen renders
 * straight from `user` — so each has to be followed by a re-read, and a caller
 * that forgets leaves the list showing what it showed before. Rather than
 * repeat that at each call site, the re-read happens here and cannot be
 * skipped.
 *
 * It does not live in the service layer: that is plain axios with no React in
 * it, and `refreshUser` comes from context. Binding them there would tie the
 * transport to the provider and make the services unusable outside it. This
 * hook is the seam between the two.
 *
 * The access token is bound here too, for the same reason — it is the same
 * token on every call, and threading it through each caller only creates one
 * more thing to get wrong.
 */
export default function useProfessionalProfile() {
  const { token, refreshUser } = useUserState();

  const run = useCallback(
    async <T>(call: (authToken: string) => Promise<T>): Promise<T> => {
      // Thrown rather than returned as a no-op: a mutation that silently does
      // nothing would report success to the screen that asked for it.
      if (!token) throw new Error("Missing access token");
      const result = await call(token);
      await refreshUser();
      return result;
    },
    [token, refreshUser]
  );

  return useMemo(
    () => ({
      /** Added from the personal data screen, so never the primary address. */
      addEmail: (value: string) =>
        run(authToken => addEmail({ authToken, value, personal: true })),
      removeEmail: (emailId: string) =>
        run(authToken => removeEmail({ authToken, emailId })),
      addPersonalPhone: (phone: PhoneNumberInput) =>
        run(authToken => addPersonalPhone({ authToken, phone })),
      removePersonalPhone: (personalPhoneId: string) =>
        run(authToken => removePersonalPhone({ authToken, personalPhoneId })),
      addAddress: (input: CreateAddressInput) =>
        run(authToken => addAddress({ authToken, input })),
      removeAddress: (addressId: string) =>
        run(authToken => removeAddress({ authToken, addressId }))
    }),
    [run]
  );
}
